import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import type { ListMyMarketplaceOffers200 } from "@packages/api-client/types/marketplaceController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Clock,
	MessageCircle,
	PackagePlus,
	Pencil,
	Plus,
	RefreshCw,
	Store,
	Tag,
	Upload,
	X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useCancelMarketplaceOffer,
	useListMyMarketplaceOffers,
	usePublishMarketplaceOffer,
	useResyncMarketplaceOffer,
} from "@/api/marketplace/offers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { isOrgOwnerOrAdmin } from "@/utils/roleUtils";
import { cn } from "@/utils/utils";
import { CowPrintArt } from "../-components/CowPrintArt";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/marketplace/my-offers/",
)({
	component: MyOffersPage,
});

type Offer = ListMyMarketplaceOffers200[number];
type OfferStatus = Offer["status"];

/* -------------------------------------------------------------------------- */
/*  Status presentation                                                       */
/* -------------------------------------------------------------------------- */

const STATUS_ORDER = [
	"DRAFT",
	"PUBLISHED",
	"RESERVED",
	"SOLD",
	"CANCELLED",
	"EXPIRED",
] as const;

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
	DRAFT: { badge: "bg-warning/15 text-warning", dot: "bg-warning" },
	PUBLISHED: { badge: "bg-success/15 text-success", dot: "bg-success" },
	RESERVED: { badge: "bg-info/15 text-info", dot: "bg-info" },
	SOLD: { badge: "bg-primary/15 text-primary", dot: "bg-primary" },
	CANCELLED: { badge: "bg-error/10 text-error", dot: "bg-error" },
	EXPIRED: {
		badge: "bg-base-200 text-base-content/50",
		dot: "bg-base-content/40",
	},
};

function StatusBadge({ status }: { status: OfferStatus }) {
	const { t } = useTranslation();
	const s = STATUS_STYLES[status] ?? STATUS_STYLES.DRAFT;
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
				s.badge,
			)}
		>
			<span className={cn("size-1.5 rounded-full", s.dot)} />
			{t(`marketplace.offerStatus.${status}`, status)}
		</span>
	);
}

function daysUntil(iso?: string): number | null {
	if (!iso) return null;
	return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/* -------------------------------------------------------------------------- */
/*  Offer card                                                                */
/* -------------------------------------------------------------------------- */

function OfferCard({
	offer,
	orgId,
	index,
	canSell,
	onPublish,
	onResync,
	onCancel,
	pending,
}: {
	offer: Offer;
	orgId: string;
	index: number;
	canSell: boolean;
	onPublish: () => void;
	onResync: () => void;
	onCancel: () => void;
	pending: boolean;
}) {
	const { t } = useTranslation();
	const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
	const big =
		offer.askingTotal != null
			? `${offer.currency} ${offer.askingTotal.toLocaleString()}`
			: `${offer.currency} ${offer.unitPrice.toLocaleString()}`;
	const pricingLabel = t(
		`livestock.sales.pricingModels.${offer.pricingModel}`,
		offer.pricingModel,
	);
	const expiresInDays =
		offer.status === "PUBLISHED" ? daysUntil(offer.expiresAt) : null;
	const canResync =
		canSell &&
		offer.offerKind === "WHOLE_HERD" &&
		(offer.status === "DRAFT" || offer.status === "PUBLISHED");
	const canCancel =
		canSell && (offer.status === "DRAFT" || offer.status === "PUBLISHED");
	const canEdit =
		canSell && (offer.status === "DRAFT" || offer.status === "PUBLISHED");
	// Only PUBLISHED + live offers resolve on the public detail page; gating the
	// card link here avoids opening a draft to a "not found" page.
	const canOpen = offer.status === "PUBLISHED";

	return (
		<div
			className={cn(
				"group relative flex flex-col gap-4 rounded-xl border border-base-300 bg-base-100 p-3 shadow-sm transition-all duration-200 animate-in fade-in slide-in-from-bottom-2 hover:border-primary/30 hover:shadow-md sm:flex-row sm:items-center",
				canOpen && "cursor-pointer",
			)}
			style={{ animationDelay: `${Math.min(index * 60, 360)}ms` }}
		>
			{/* Thumbnail — real cover photo when present, generated art otherwise */}
			<div className="relative size-full shrink-0 overflow-hidden rounded-lg sm:size-28">
				{offer.coverImageUrl ? (
					<img
						src={offer.coverImageUrl}
						alt={offer.title}
						className="h-32 w-full object-cover sm:h-full"
					/>
				) : (
					<CowPrintArt seed={offer.id} className="h-32 w-full sm:h-full" />
				)}
				<span className="absolute bottom-1.5 left-1.5 rounded bg-stone-900/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
					{t("marketplace.headCount", { n: offer.headCount })}
				</span>
			</div>

			{/* Body */}
			<div className="flex min-w-0 flex-1 flex-col gap-1.5">
				<div className="flex flex-wrap items-center gap-2">
					<StatusBadge status={offer.status} />
					<span className="inline-flex items-center gap-1 rounded-full bg-base-200 px-2 py-0.5 text-xs font-medium text-base-content/70">
						<Tag className="size-3" />
						{t(`marketplace.kind.${offer.offerKind}`, offer.offerKind)}
					</span>
					{expiresInDays != null && expiresInDays >= 0 && (
						<span className="inline-flex items-center gap-1 text-xs text-base-content/50">
							<Clock className="size-3" />
							{t("marketplace.myOffers.expiresIn", { n: expiresInDays })}
						</span>
					)}
				</div>
				{canOpen ? (
					<Link
						to="/app/org/$orgId/marketplace/$offerId"
						params={{ orgId, offerId: offer.id }}
						className="min-w-0 after:absolute after:inset-0"
					>
						<h2 className="truncate text-base font-semibold leading-snug text-base-content transition-colors group-hover:text-primary">
							{offer.title}
						</h2>
					</Link>
				) : (
					<h2 className="truncate text-base font-semibold leading-snug text-base-content">
						{offer.title}
					</h2>
				)}
				<div className="flex items-baseline gap-2">
					<span className="text-lg font-bold leading-none text-primary">
						{big}
					</span>
					<span className="text-xs text-base-content/55">
						{offer.askingTotal != null
							? t("marketplace.headCount", { n: offer.headCount })
							: `/ ${pricingLabel}`}
					</span>
				</div>
			</div>

			{/* Actions — lifted above the card's overlay link so clicks don't navigate */}
			<div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
				{canSell && offer.status === "DRAFT" && (
					<Button
						size="sm"
						onClick={onPublish}
						disabled={pending}
						className="rounded-full"
					>
						<Upload className="size-3.5" />
						{t("marketplace.myOffers.publish")}
					</Button>
				)}
				{canEdit && (
					<Button asChild size="sm" variant="outline" className="rounded-full">
						<Link
							to="/app/org/$orgId/marketplace/my-offers/$offerId/edit"
							params={{ orgId, offerId: offer.id }}
						>
							<Pencil className="size-3.5" />
							{t("marketplace.myOffers.edit")}
						</Link>
					</Button>
				)}
				{canResync && (
					<Button
						size="sm"
						variant="outline"
						onClick={onResync}
						disabled={pending}
						className="rounded-full"
					>
						<RefreshCw className="size-3.5" />
						{t("marketplace.myOffers.resync")}
					</Button>
				)}
				<Button
					asChild
					size="sm"
					variant="ghost"
					className="rounded-full text-base-content/70"
				>
					<Link
						to="/app/org/$orgId/marketplace/incoming-requests"
						params={{ orgId }}
					>
						<MessageCircle className="size-3.5" />
						{t("marketplace.myOffers.requests")}
					</Link>
				</Button>
				{canCancel && (
					<>
						<Button
							size="icon"
							variant="ghost"
							onClick={() => setCancelConfirmOpen(true)}
							disabled={pending}
							aria-label={t("common.cancel")}
							title={t("common.cancel")}
							className="rounded-full text-base-content/40 hover:text-error"
						>
							<X className="size-4" />
						</Button>
						<AlertDialog
							open={cancelConfirmOpen}
							onOpenChange={setCancelConfirmOpen}
						>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>
										{t(
											"marketplace.myOffers.cancelConfirmTitle",
											"Cancel this offer?",
										)}
									</AlertDialogTitle>
									<AlertDialogDescription>
										{t(
											"marketplace.myOffers.cancelConfirmBody",
											"This removes the offer from the marketplace. This action can't be undone.",
										)}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel
										disabled={pending}
										onClick={() => setCancelConfirmOpen(false)}
									>
										{t("marketplace.myOffers.cancelConfirmKeep", "Keep offer")}
									</AlertDialogCancel>
									<AlertDialogAction
										disabled={pending}
										onClick={() => {
											setCancelConfirmOpen(false);
											onCancel();
										}}
									>
										{t("common.cancel")}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</>
				)}
			</div>
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function MyOffersPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const { data: offers, isLoading } = useListMyMarketplaceOffers(orgId);
	const { data: organizations } = useSuspenseQuery({
		...getMyOrganizationsQueryOptions(),
		staleTime: Number.POSITIVE_INFINITY,
	});
	// UX gate only — the backend's `assertOwnerOrAdmin` is the real control.
	const canSell = isOrgOwnerOrAdmin(
		organizations.find((org) => org.id === orgId)?.role,
	);

	const publish = usePublishMarketplaceOffer();
	const cancel = useCancelMarketplaceOffer();
	const resync = useResyncMarketplaceOffer();
	const pending = publish.isPending || cancel.isPending || resync.isPending;

	const [filter, setFilter] = useState<OfferStatus | "ALL">("ALL");

	const list = useMemo(() => offers ?? [], [offers]);
	const counts = useMemo(() => {
		const c: Record<string, number> = {};
		for (const o of list) c[o.status] = (c[o.status] ?? 0) + 1;
		return c;
	}, [list]);
	const visible = useMemo(
		() => (filter === "ALL" ? list : list.filter((o) => o.status === filter)),
		[list, filter],
	);
	const activeStatuses = STATUS_ORDER.filter((s) => (counts[s] ?? 0) > 0);

	const createButton = (
		<Button asChild className="rounded-full">
			<Link to="/app/org/$orgId/marketplace/my-offers/new" params={{ orgId }}>
				<Plus className="size-4" />
				{t("marketplace.myOffers.create")}
			</Link>
		</Button>
	);

	return (
		<div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
			{/* Hero header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-3">
					<div className="grid size-11 place-items-center rounded-xl bg-primary text-primary-content shadow-sm">
						<Store className="size-6" />
					</div>
					<div>
						<h1 className="text-2xl font-bold tracking-tight">
							{t("marketplace.myOffers.title")}
						</h1>
						<p className="text-sm text-base-content/60">
							{t("marketplace.myOffers.subtitle")}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button asChild variant="outline" className="rounded-full">
						<Link to="/app/org/$orgId/marketplace" params={{ orgId }}>
							{t("marketplace.myOffers.back")}
						</Link>
					</Button>
					{canSell && createButton}
				</div>
			</div>

			{/* Status filter / overview pills */}
			{!isLoading && list.length > 0 && (
				<div className="mt-6 flex flex-wrap gap-2">
					<FilterPill
						active={filter === "ALL"}
						onClick={() => setFilter("ALL")}
						label={t("marketplace.myOffers.allStatuses")}
						count={list.length}
					/>
					{activeStatuses.map((s) => (
						<FilterPill
							key={s}
							active={filter === s}
							onClick={() => setFilter(s)}
							label={t(`marketplace.offerStatus.${s}`, s)}
							count={counts[s] ?? 0}
							dot={STATUS_STYLES[s]?.dot}
						/>
					))}
				</div>
			)}

			{/* Loading skeletons */}
			{isLoading && (
				<div className="mt-6 space-y-3">
					{["a", "b", "c"].map((k) => (
						<div
							key={k}
							className="flex gap-4 rounded-xl border border-base-300 bg-base-100 p-3"
						>
							<div className="size-28 shrink-0 animate-pulse rounded-lg bg-base-200" />
							<div className="flex-1 space-y-2 py-2">
								<div className="h-4 w-24 animate-pulse rounded-full bg-base-200" />
								<div className="h-5 w-2/3 animate-pulse rounded bg-base-200" />
								<div className="h-5 w-1/3 animate-pulse rounded bg-base-200" />
							</div>
						</div>
					))}
				</div>
			)}

			{/* Empty state */}
			{!isLoading && list.length === 0 && (
				<div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-base-300 bg-base-100 px-6 py-16 text-center">
					<div className="grid size-16 place-items-center rounded-2xl bg-primary/10 text-primary">
						<PackagePlus className="size-8" />
					</div>
					<h2 className="mt-5 text-lg font-semibold">
						{t("marketplace.myOffers.emptyTitle")}
					</h2>
					<p className="mt-1 max-w-sm text-sm text-base-content/60">
						{t("marketplace.myOffers.emptyHint")}
					</p>
					{canSell && (
						<Button asChild className="mt-6 rounded-full">
							<Link
								to="/app/org/$orgId/marketplace/my-offers/new"
								params={{ orgId }}
							>
								<Plus className="size-4" />
								{t("marketplace.myOffers.emptyCta")}
							</Link>
						</Button>
					)}
				</div>
			)}

			{/* Offer cards */}
			{!isLoading && visible.length > 0 && (
				<div className="mt-4 space-y-3">
					{visible.map((offer, i) => (
						<OfferCard
							key={offer.id}
							offer={offer}
							orgId={orgId}
							index={i}
							canSell={canSell}
							pending={pending}
							onPublish={() =>
								publish.mutate({ organizationId: orgId, offerId: offer.id })
							}
							onResync={() =>
								resync.mutate({ organizationId: orgId, offerId: offer.id })
							}
							onCancel={() =>
								cancel.mutate({ organizationId: orgId, offerId: offer.id })
							}
						/>
					))}
				</div>
			)}

			{/* Filtered-empty */}
			{!isLoading && list.length > 0 && visible.length === 0 && (
				<p className="mt-8 text-center text-sm text-base-content/50">
					{t("marketplace.myOffers.empty")}
				</p>
			)}
		</div>
	);
}

function FilterPill({
	active,
	onClick,
	label,
	count,
	dot,
}: {
	active: boolean;
	onClick: () => void;
	label: string;
	count: number;
	dot?: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={cn(
				"inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
				active
					? "border-primary bg-primary text-primary-content shadow-sm"
					: "border-base-300 bg-base-100 text-base-content/80 hover:border-primary/40 hover:bg-base-200",
			)}
		>
			{dot && (
				<span
					className={cn(
						"size-1.5 rounded-full",
						active ? "bg-primary-content" : dot,
					)}
				/>
			)}
			{label}
			<span
				className={cn(
					"rounded-full px-1.5 text-xs font-semibold",
					active ? "bg-primary-content/20" : "bg-base-200 text-base-content/60",
				)}
			>
				{count}
			</span>
		</button>
	);
}
